/**
 * Utility functions for language handling throughout the application
 */

interface LanguageInfo {
  code: string;
  name: string;
}

/**
 * Map of language names to proper BCP 47 language tags
 */
export const LANGUAGE_CODES: Record<string, string> = {
  english: 'en-US',
  dutch: 'nl-NL',
  hebrew: 'he-IL',
  spanish: 'es-ES',
  french: 'fr-FR',
  german: 'de-DE',
  italian: 'it-IT',
  portuguese: 'pt-PT',
  russian: 'ru-RU',
  chinese: 'zh-CN',
  japanese: 'ja-JP',
  korean: 'ko-KR',
  arabic: 'ar-SA',
};

/**
 * Language info for UI display
 */
export const LANGUAGES: LanguageInfo[] = Object.entries(LANGUAGE_CODES).map(([name, code]) => ({
  code,
  name: name.charAt(0).toUpperCase() + name.slice(1),
}));

/**
 * Converts a language name to the proper BCP 47 language tag for TTS
 * @param language The language name (e.g., 'english', 'dutch') or language code (e.g., 'en-US')
 * @returns The proper language code (e.g., 'en-US', 'nl-NL')
 */
export function getLanguageCode(language: string): string {
  // Default to English if language is not specified
  if (!language) return LANGUAGE_CODES.english;

  // Normalize language name (lowercase) and look up in our mapping
  const normalizedLang = language.toLowerCase();

  // Check if it's already a BCP-47 code (e.g., contains a hyphen)
  if (normalizedLang.includes('-')) {
    // Check if it matches one of our known language codes
    const isKnownCode = Object.values(LANGUAGE_CODES).some(
      code => code.toLowerCase() === normalizedLang
    );

    // If it's already a valid BCP-47 code, return it as is
    if (isKnownCode) {
      return language;
    }

    // If it's not a known code, try to derive the language name from the first part
    const baseCode = normalizedLang.split('-')[0];
    // Find corresponding language name
    const languageName = Object.entries(LANGUAGE_CODES).find(
      ([_, code]) => code.toLowerCase().startsWith(baseCode)
    )?.[0];

    if (languageName) {
      return LANGUAGE_CODES[languageName];
    }
  }

  // Return the mapped language code or default to the original if not found
  return LANGUAGE_CODES[normalizedLang] || language;
}